import React from 'react';
import { Box, ButtonProps, Typography } from '@material-ui/core';
import styled from 'styled-components';

import { Logo } from 'common/components/Logo';
import { Navigation } from 'common/components/Navigation/Navigation';
import { PageFooter } from 'common/components/PageFooter';
import { Loader } from 'common/components/Loader/Loader';
import { Link } from 'common/components/Link';
import { Container } from 'common/components/Container';
import { getIntlPath } from 'common/utils';

const LeftSection = styled(Box)`
    position: relative;
    display: flex;
    width: 100%;
    height: 80px;
    background-color: ${({ theme }) => theme.palette.background.paper};
    padding: ${({ theme }) => `0 ${theme.spacing(5)}px`};
    z-index: 1;

    .logoLink {
        text-decoration: none;
    }

    ${Logo} {
        height: 100%;

        .logoWrapper {
            width: 54px;
            margin-right: ${({ theme }) => `${theme.spacing(1)}px`};
        }

        .MuiTypography-root {
            font-size: ${({ theme }) => theme.typography.body2.fontSize};
            line-height: 1;
        }

        svg {
            height: 36px;
        }
    }

    ${Navigation} {
        margin-left: auto;
    }

    @media (min-width: ${({ theme }) =>
            `${theme.breakpoints.values['md']}px`}) {
        position: fixed;
        max-width: 340px;
        height: 100%;
        align-items: center;
        flex-direction: column;
        padding: ${({ theme }) =>
            `${theme.spacing(12)}px ${theme.spacing(5)}px`};
        z-index: 0;

        .logoLink {
            margin-bottom: ${({ theme }) => `${theme.spacing(14)}px`};
        }

        ${Navigation} {
            width: 100%;
            height: 100%;
            margin-left: 0;
        }

        ${Logo} {
            height: 100%;

            .logoWrapper {
                width: 95px;
                margin-right: ${({ theme }) => `${theme.spacing(1)}px`};
            }

            .MuiTypography-root {
                font-size: 2rem;
                line-height: 1;
            }

            svg {
                height: 65px;
            }
        }
    }
`;

const RightSection = styled(Box)`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    min-height: 100%;
    flex: 1;
    z-index: 0;

    .content-container {
        display: flex;
        flex-direction: column;
        min-height: 100%;
    }

    .pageWrapper {
        padding-top: ${({ theme }) => `${theme.spacing(13)}px`};
        padding-bottom: ${({ theme }) => `${theme.spacing(8)}px`};
    }

    .pageTop {
        display: block;
    }

    .pageTitle {
        margin-bottom: ${({ theme }) => `${theme.spacing(8)}px`};
    }

    .newButton {
        position: relative;
        width: 100%;
        margin-bottom: ${({ theme }) => `${theme.spacing(8)}px`};
    }

    .MuiSnackbar-root {
        margin-top: 100px;
    }

    @media (min-width: ${({ theme }) =>
            `${theme.breakpoints.values['sm']}px`}) {
        .pageTop {
            display: flex;
        }

        .newButton {
            width: auto;
            max-width: 335px;
            margin-left: auto;
            top: -5px;
            margin-bottom: 0;
        }
    }

    @media (min-width: ${({ theme }) =>
            `${theme.breakpoints.values['md']}px`}) {
        position: absolute;
        left: ${({ theme }) => `${theme.spacing(68)}px`};
        flex: auto;
        width: ${({ theme }) => `calc(100% - ${theme.spacing(68)}px)`};

        .pageWrapper {
            padding-bottom: ${({ theme }) => `${theme.spacing(120)}px`};
        }

        .pageTitle {
            margin-bottom: ${({ theme }) => `${theme.spacing(8)}px`};
        }

        .MuiSnackbar-root {
            margin-top: 0;
        }
    }
`;

interface IProps {
    basic?: boolean;
    Button?: React.ReactElement<ButtonProps>;
    className?: string;
    loading?: boolean;
    pageTitle?: string;
}

const PageWrapper: React.FC<IProps> = ({
    basic,
    Button,
    children,
    className,
    loading,
    pageTitle,
}) => (
    <Box className={className}>
        <LeftSection>
            <Link className="logoLink" to={getIntlPath('invitations')}>
                <Logo />
            </Link>

            {!basic && <Navigation />}
        </LeftSection>
        <RightSection>
            <Container className="content-container" maxWidth="lg">
                {loading ? (
                    <Box
                        alignItems="center"
                        display="flex"
                        flex="1"
                        justifyContent="center"
                    >
                        <Loader size={60} />
                    </Box>
                ) : (
                    <Box className="pageWrapper">
                        <Box className="pageTop" display="flex">
                            {!!pageTitle && (
                                <Box
                                    className={`pageTitle ${
                                        Button ? 'hasButton' : ''
                                    }`}
                                >
                                    <Typography color="primary" variant="h1">
                                        {pageTitle}
                                    </Typography>
                                </Box>
                            )}

                            {!!Button && (
                                <Box className="newButton">{Button}</Box>
                            )}
                        </Box>

                        {children}
                    </Box>
                )}
            </Container>

            {!basic && <PageFooter />}
        </RightSection>
    </Box>
);

export const BasePage = styled(PageWrapper)`
    display: flex;
    min-height: 100%;
    background-color: ${({ theme }) => theme.palette.background.default};
    flex-direction: column;

    ${PageFooter} {
        margin-top: auto;
    }

    @media (min-width: ${({ theme }) =>
            `${theme.breakpoints.values['md']}px`}) {
        flex-direction: row;
    }
`;
